import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const DrawerCountsList = ({ reconciliations, onApprove, onDispute, onGenerateSettlement }) => {
  const [expandedId, setExpandedId] = useState(null);
  const [disputeReason, setDisputeReason] = useState('');
  const [showDisputeModal, setShowDisputeModal] = useState(false);
  const [selectedReconciliation, setSelectedReconciliation] = useState(null);

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: 'Pending', color: 'bg-orange-500/10 text-orange-500 border-orange-500/20' },
      approved: { label: 'Approved', color: 'bg-green-500/10 text-green-500 border-green-500/20' },
      disputed: { label: 'Disputed', color: 'bg-red-500/10 text-red-500 border-red-500/20' }
    };

    const config = statusConfig?.[status] || statusConfig?.pending;

    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium border ${config?.color}`}>
        {config?.label}
      </span>
    );
  };

  const handleDisputeClick = (reconciliation) => {
    setSelectedReconciliation(reconciliation);
    setShowDisputeModal(true);
  };

  const handleDisputeSubmit = () => {
    if (!disputeReason?.trim()) {
      alert('Please provide a reason for the dispute');
      return;
    }
    onDispute(selectedReconciliation?.id, disputeReason);
    setShowDisputeModal(false);
    setDisputeReason('');
    setSelectedReconciliation(null);
  };

  if (!reconciliations || reconciliations?.length === 0) {
    return (
      <div className="bg-card rounded-lg shadow-md p-8 border border-border text-center">
        <Icon name="Inbox" size={48} className="text-muted-foreground mx-auto mb-4" />
        <p className="text-muted-foreground">No reconciliation records found for the selected date</p>
      </div>
    );
  }

  return (
    <>
      <div className="mb-6">
        <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
          <Icon name="List" size={24} className="text-primary" />
          Drawer Reconciliations ({reconciliations?.length})
        </h2>
        <div className="space-y-4">
          {reconciliations?.map((reconciliation) => {
            const variance = reconciliation?.actual_balance - reconciliation?.expected_balance;
            const isExpanded = expandedId === reconciliation?.id;

            return (
              <div
                key={reconciliation?.id}
                className="bg-card rounded-lg shadow-md border border-border overflow-hidden"
              >
                {/* Header */}
                <div className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-bold text-foreground">
                          {reconciliation?.cash_drawers?.name || 'Drawer'}
                        </h3>
                        {getStatusBadge(reconciliation?.status)}
                      </div>
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Icon name="User" size={14} />
                          {reconciliation?.user_profiles?.full_name || 'Unknown'}
                        </span>
                        <span className="flex items-center gap-1">
                          <Icon name="Clock" size={14} />
                          {new Date(reconciliation?.created_at)?.toLocaleTimeString()}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Balance Summary */}
                  <div className="grid grid-cols-3 gap-4 p-3 bg-background rounded-lg border border-border">
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Expected</p>
                      <p className="text-lg font-bold text-foreground">${reconciliation?.expected_balance?.toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Actual</p>
                      <p className="text-lg font-bold text-primary">${reconciliation?.actual_balance?.toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Variance</p>
                      <p className={`text-lg font-bold ${
                        variance === 0 ? 'text-green-500' : 
                        Math.abs(variance) <= 5 ? 'text-orange-500' : 'text-red-500'
                      }`}>
                        {variance >= 0 ? '+' : ''}${variance?.toFixed(2)}
                      </p>
                    </div>
                  </div>

                  {/* Variance Reason */}
                  {reconciliation?.variance_reason && (
                    <div className="mt-3 p-3 bg-orange-500/10 rounded-lg border border-orange-500/20">
                      <p className="text-sm font-medium text-foreground mb-1">Variance Reason:</p>
                      <p className="text-sm text-muted-foreground">{reconciliation?.variance_reason}</p>
                    </div>
                  )}

                  {/* Notes */}
                  {reconciliation?.notes && (
                    <div className="mt-3 p-3 bg-background rounded-lg border border-border">
                      <p className="text-sm font-medium text-foreground mb-1">Notes:</p>
                      <p className="text-sm text-muted-foreground">{reconciliation?.notes}</p>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex flex-wrap gap-2 mt-4">
                    <Button
                      size="sm"
                      variant="outline"
                      iconName={isExpanded ? 'ChevronUp' : 'ChevronDown'}
                      iconPosition="right"
                      onClick={() => setExpandedId(isExpanded ? null : reconciliation?.id)}
                    >
                      {isExpanded ? 'Hide' : 'Show'} Denomination Breakdown
                    </Button>
                    {reconciliation?.status === 'pending' && (
                      <>
                        <Button
                          size="sm"
                          iconName="CheckCircle"
                          iconPosition="left"
                          onClick={() => onApprove(reconciliation?.id)}
                        >
                          Approve
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          iconName="AlertTriangle"
                          iconPosition="left"
                          onClick={() => handleDisputeClick(reconciliation)}
                        >
                          Dispute
                        </Button>
                      </>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      iconName="FileText"
                      iconPosition="left"
                      onClick={() => onGenerateSettlement(reconciliation)}
                    >
                      Generate Settlement
                    </Button>
                  </div>
                </div>

                {/* Denomination Breakdown */}
                {isExpanded && reconciliation?.cash_denominations && (
                  <div className="border-t border-border p-4 bg-background">
                    <h4 className="font-semibold text-foreground mb-3">Denomination Breakdown</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {reconciliation?.cash_denominations?.map((denom, idx) => (
                        <div key={idx} className="p-3 bg-card rounded-lg border border-border">
                          <div className="flex items-center gap-2 mb-1">
                            <Icon 
                              name={denom?.denomination_type === 'bill' ? 'Banknote' : 'Coins'} 
                              size={16} 
                              className="text-primary" 
                            />
                            <span className="text-sm font-medium text-foreground">
                              ${denom?.denomination_value?.toFixed(2)}
                            </span>
                          </div>
                          <p className="text-xs text-muted-foreground">Qty: {denom?.quantity}</p>
                          <p className="text-sm font-bold text-foreground mt-1">
                            ${(denom?.denomination_value * denom?.quantity)?.toFixed(2)}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Dispute Modal */}
      {showDisputeModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg shadow-xl max-w-md w-full p-6 border border-border">
            <div className="flex items-center gap-3 mb-4">
              <Icon name="AlertTriangle" size={24} className="text-red-500" />
              <h3 className="text-xl font-bold text-foreground">Dispute Reconciliation</h3>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              Please provide a reason for disputing this reconciliation:
            </p>
            <textarea
              value={disputeReason}
              onChange={(e) => setDisputeReason(e?.target?.value)}
              placeholder="Explain the issue..."
              rows={4}
              className="w-full px-4 py-2 rounded-lg bg-background border border-border focus:outline-none focus:ring-2 focus:ring-primary text-foreground resize-none mb-4"
            />
            <div className="flex gap-2">
              <Button
                onClick={handleDisputeSubmit}
                iconName="AlertTriangle"
                iconPosition="left"
                fullWidth
              >
                Submit Dispute
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setShowDisputeModal(false);
                  setDisputeReason('');
                  setSelectedReconciliation(null);
                }}
                fullWidth
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DrawerCountsList;